import { ChangeEvent, FC, useMemo } from 'react'
import styled, { css } from 'styled-components'

import Box from 'components/Box'
import Text from 'components/Text'
import { ErrorMessage } from 'components/TextInput'

import { colors } from 'utils/colors'

import { CheckboxProps } from '../types'
import { WidthProps } from 'types/styled'

type Props = Omit<CheckboxProps, 'width'> & {
  width?: WidthProps
}

const spacing = 2
const transition = '0.4s'
const totalWidth = 34
const tinyDimensions = 14
const transform = totalWidth - tinyDimensions - spacing * 2

const Switch: FC<Props> = ({
  checked,
  children,
  disabled,
  error,
  onChange,
  showError,
  width = 'auto',
  ...marginProps
}) => {
  const label = useMemo(
    () =>
      typeof children === 'string' || children instanceof String ? (
        <Text ellipsis whiteSpace="nowrap">
          {children}
        </Text>
      ) : (
        children
      ),
    [children],
  )

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.currentTarget.checked)
  }

  return (
    <Box {...marginProps} width={width}>
      <Box alignItems="center" display="flex">
        <StyledLabel>
          <StyledInput checked={checked} disabled={disabled} onChange={handleChange} type="checkbox" />
          <Slider checked={Boolean(checked)} disabled={Boolean(disabled)} />
        </StyledLabel>
        {label}
      </Box>
      {showError && error && <ErrorMessage>{error}</ErrorMessage>}
    </Box>
  )
}

const sliderBg = (checked: boolean, disabled: boolean) => {
  if (checked) {
    if (disabled) {
      return colors.blue7
    }
    return colors.blue1
  }

  return colors.gray4
}

const Slider = styled.span<{ checked: boolean; disabled: boolean }>`
  background-color: ${(p) => sliderBg(p.checked, p.disabled)};
  border-radius: 12px;
  bottom: 0;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  left: 0;
  position: absolute;
  right: 0;
  transition: ${transition};
  top: 0;

  &::before {
    background-color: ${(p) => (p.disabled ? p.theme.colors.gray2 : p.theme.colors.white)};
    border-radius: 50%;
    bottom: ${spacing}px;
    content: '';
    cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
    height: ${tinyDimensions}px;
    left: ${spacing}px;
    position: absolute;
    transition: ${transition};
    width: ${tinyDimensions}px;
    ${(p) =>
      p.checked &&
      css`
        transform: translateX(${transform}px);
      `}
  }
`

const StyledInput = styled.input`
  height: 0;
  opacity: 0;
  width: 0;
`

const StyledLabel = styled.label`
  height: 18px;
  margin-right: 16px;
  position: relative;
  width: ${totalWidth}px;
`

export default Switch
